<script setup>
import { inject, ref } from 'vue';
import { useOrderStore } from '@/stores/order';
import { storeToRefs } from 'pinia';
import { useToast } from 'vue-toastification';
import { trans } from 'laravel-vue-i18n';
import { useDateFormatter } from '@/composables/dateFormatter.vue';

const { formatDateTime } = useDateFormatter();
const modals = inject('modals');
const toast = useToast();
const { convertQuote } = useOrderStore();
const showDebugger = ref(false);

const formatQuantity = (value) => Number(value).toFixed(2);

const formatPrice = value => {
    return new Intl.NumberFormat('en-CA', {
        style: 'currency',
        currency: 'CAD',
    }).format(value);
};

// Get the active order from the store
const { active, price } = storeToRefs(useOrderStore());

const getCompoundSubtotal = (compound) => {
    const breakdown = price.value?.compound_breakdown?.find(
        (item) => item['Compound ID'] === compound.id,
    );
    return breakdown?.['Compound Total'];
};

const convert = async () => {
    if (active.value?.type?.name === 'Order') {
        toast.error(trans('label.order_already_converted'));
        return;
    }

    const confirm = await modals.show('ConfirmationModal', {
        props: {
            title: trans('label.confirm_convert_quote'),
            prompt: trans('label.are_you_sure_convert_quote'),
        }
    });

    if (confirm) {
        try {
            await convertQuote(active.value);
            toast.success(trans('label.quote_converted_to_order'));
        } catch (error) {
            toast.error(trans('action.convert.error'));
        }
    }
};
</script>

<template>
<MainContentLayout>
    <template #breadcrumb>
        <BreadCrumb />
    </template>
    <template #actions>
        <StyledButton
            v-if="active?.type?.name === 'Quote'"
            size="large"
            @click="convert">
            {{ $t('label.convert_quote') }}
        </StyledButton>
    </template>

    <v-card v-if="active" color="primary" class="mb-4 shadow-lg">
        <v-card-text>
            <div class="p-4 md:p-8 flex flex-col gap-4 text-base md:text-lg">
                <div class="font-bold text-lg md:text-xl">
                    {{ $t("label.order") }} #{{ active.id }} -
                    {{ active?.status?.name }}
                </div>
                <div>
                    <strong>{{ $t("label.pharmacy") }}:</strong>
                    {{ active?.pharmacy?.name }}
                </div>
                <div>
                    <strong>{{ $t("label.order_placed") }}:</strong>
                    {{ formatDateTime(active.created_at) }}
                </div>
                <div>
                    <strong>{{ $t("label.rx_number") }}:</strong>
                    {{ active.rx_number }}
                </div>
                <div>
                    <strong>{{ $t("label.total") }}:</strong>
                    {{ formatPrice(price.total) }}
                </div>
            </div>
        </v-card-text>
    </v-card>

    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div
            v-for="(compound, index) in active.compounds"
            :key="`compound_${index}`"
            class="shadow-lg">
            <div
                class="flex flex-col md:flex-row justify-between bg-grey-700 text-white p-4 md:p-8 text-base md:text-lg font-bold rounded-t">
                <div class="flex flex-col gap-1">
                    <div>
                        {{ compound.name }} ({{
                            compound.compound_type.display_name
                        }}<span v-if="compound.is_sterile">{{ $t("label.comma") }} {{ $t("label.sterile") }}</span>)
                    </div>
                    <div v-if="compound.expiry" class="text-md font-normal text-gray-300">
                        {{ $t('label.expiry') }}: {{ compound.expiry }} {{ $t("label.days") }}
                    </div>
                </div>
                <div class="flex flex-col items-end md:items-end gap-4 text-right">
                    <div class="text-gray-500 text-sm md:text-base">
                        {{ $t("label.quantity") }}:
                        {{ formatQuantity(compound.pivot.quantity) }}x
                    </div>
                    <div v-if="getCompoundSubtotal(compound) != null" class="text-gray-500 text-sm md:text-base">
                        {{ $t("label.compound_total") }}:
                        {{ formatPrice(getCompoundSubtotal(compound)) }}
                    </div>
                </div>
            </div>
            <div class="p-4 md:p-8">
                <div
                    v-for="(product, index2) in compound.products"
                    :key="`product_${index2}`"
                    class="flex justify-between border-b py-2 md:py-4">
                    <div>
                        <div class="font-medium">
                            {{ product.name }}
                        </div>
                        <div class="text-gray-500 text-sm md:text-base">
                            {{ product.unit.display_name }}
                        </div>
                        <div>
                            <div v-if="product.pivot.percentage != 0">{{ (product.pivot.percentage * compound.pivot.quantity / 100).toFixed(2) }}</div>
                            <div v-if="product.pivot.quantity != 0">{{ $t("label.quantity") }}: {{ product.pivot.quantity }}</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
    <div v-if="active?.type?.name !== 'Quote' && active?.notes?.length" class="mt-8">
        <v-card color="grey-100" class="shadow-lg">
            <v-card-text>
                <div class="p-4 md:p-8 text-base md:text-lg">
                    <strong class="mr-2">{{ $t('label.note') }}:</strong>
                    <span>{{ active.notes[0]?.content }}</span>
                </div>
            </v-card-text>
        </v-card>
    </div>
    <div v-if="showDebugger" class="p-4 md:p-12 bg-grey-800 text-white rounded-xl shadow-lg my-6 md:my-12">
        <div class="font-bold text-base md:text-lg">
            {{ $t("label.calculator_debugger") }}
        </div>
        <div>
            <div>
                <div class="text-sm md:text-md font-bold mb-4">
                    {{ $t("label.pharmacy_fees") }}
                </div>
                <v-card outlined class="mb-6">
                    <v-card-text>
                        <div class="flex justify-between">
                            <span
                                class="font-medium">{{ $t("label.delivery_fee") }}:</span>
                            <span>{{
                                formatPrice(price.delivery_fee)
                            }}</span>
                        </div>
                        <div class="flex justify-between">
                            <span
                                class="font-medium">{{
                                    $t("label.pharmacy_delivery_rate")
                                }}:</span>
                            <span>{{
                                formatPrice(
                                    parseFloat(
                                        price.pharmacy_delivery_rate,
                                    ),
                                )
                            }}</span>
                        </div>
                    </v-card-text>
                </v-card>
                <div>
                    <div class="text-sm md:text-md font-semibold mb-4">
                        {{ $t("label.compound_breakdown") }}
                    </div>
                    <v-expansion-panels multiple>
                        <v-expansion-panel
                            v-for="compound in price.compound_breakdown"
                            :key="compound.compound_id"
                            class="mb-4">
                            <v-expansion-panel-title>
                                <span
                                    class="font-medium">{{ $t("label.name") }}:</span>
                                <span class="ml-2">{{
                                    compound.compound_name
                                }}</span>
                            </v-expansion-panel-title>
                            <v-expansion-panel-text>
                                <div
                                    v-for="(value, key) in compound"
                                    :key="key"
                                    class="flex gap-4">
                                    <span class="font-medium w-48">{{
                                        key
                                    }}:</span>
                                    <span>{{
                                        typeof value === 'number'
                                            ? key === 'Ingredient Price'
                                                ? value.toFixed(4)
                                                : key.includes('price') || key.includes('fee')
                                                    ? formatPrice(value)
                                                    : value.toFixed
                                                        ? value.toFixed(2)
                                                        : value
                                            : value || $t("label.not_available")
                                    }}</span>
                                </div>
                            </v-expansion-panel-text>
                        </v-expansion-panel>
                    </v-expansion-panels>
                </div>
                <div class="mt-6">
                    <div class="flex justify-between">
                        <span
                            class="text-base md:text-lg font-semibold">{{ $t("label.total") }}:</span>
                        <span class="text-base md:text-lg font-semibold">{{
                            formatPrice(price.total)
                        }}</span>
                    </div>
                </div>
            </div>
        </div>
    </div>
</MainContentLayout>
</template>
